import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useMainStore from "../../store/mainState";
import { useMutation, useQuery } from "@apollo/client";
import { PYBIT_ADDRESS_GET, GET_PAYMENT_METHODS } from "../../graphql/transaction";
import QRCode from "qrcode";
import { ReactComponent as BitcoinIcon } from "../../assets/icons/bitcoin.svg";
import CryptoSelect from "../../components/ui/CryptoSelect";

const CashIn = ({ setSelectedPaymentMethod }: any) => {
    const { t } = useTranslation();
    const { environment } = useMainStore();
    const [paymentMethodes, setPaymentMethodes] = useState<any>();
    const { data } = useQuery(GET_PAYMENT_METHODS, {
        variables: { type: "collect", activeOnly: true, environmentId: environment?.id },
        fetchPolicy: "cache-and-network",
        skip: !environment?.id,
    });
    const [getAddress, { loading }] = useMutation(PYBIT_ADDRESS_GET);
    const [address, setAddress] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState<string>("");

    const selectedCoin = data?.paymentMethodActions?.find(
        (m: any) => String(m.id) === String(paymentMethodes)
    ) || data?.paymentMethodActions?.[0];

    const handleCopy = async () => {
        if (address) {
            await navigator.clipboard.writeText(address);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleConfirm = async () => {
        const mainnet = paymentMethodes ? paymentMethodes : data?.paymentMethodActions?.[0]?.id;
        const getAddressData = await getAddress({
            variables: { paymentMethodActionId: +mainnet },
        });
        setPaymentMethodes(mainnet);
        setAddress(getAddressData?.data?.pybitAddressGet?.data?.address);
    };

    useEffect(() => {
        if (address) {
            QRCode.toDataURL(address, {
                width: 300,
                margin: 2,
                color: { dark: "#000000", light: "#FFFFFF" },
            })
                .then((url: string) => setQrCodeUrl(url))
                .catch((err: any) => console.error("Error generating QR code:", err));
        }
    }, [address]);

    return (
        <div className="w-full">
            {address ? (
                <div className="flex flex-col items-center gap-5">
                    {/* Selected coin info */}
                    {selectedCoin && (
                        <div
                            className="flex items-center gap-3 w-full p-3 rounded-xl"
                            style={{ background: "rgba(255, 255, 255, 0.04)" }}
                        >
                            <div className="w-9 h-9 rounded-full bg-[#4DBDFF]/10 flex items-center justify-center flex-shrink-0">
                                <BitcoinIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                                <p className="text-white text-sm font-medium">{selectedCoin.coin}</p>
                                <p className="text-white/40 text-xs">{selectedCoin.chain} network</p>
                            </div>
                            <div
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium text-[#4CAF50]"
                                style={{ background: "rgba(76, 175, 80, 0.1)", border: "1px solid rgba(76, 175, 80, 0.2)" }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#4CAF50] animate-pulse" />
                                {t('payment.active')}
                            </div>
                        </div>
                    )}

                    {/* Address label */}
                    <div className="w-full">
                        <p className="text-white/50 text-xs font-medium mb-2">{t('payment.depositAddress')}</p>
                        <div className="relative w-full">
                            <input
                                type="text"
                                value={address}
                                readOnly
                                className="w-full text-white text-xs font-mono px-4 py-3.5 pr-14 rounded-xl appearance-none focus:outline-none transition-all duration-200"
                                style={{
                                    background: "rgba(255, 255, 255, 0.06)",
                                    border: "1px solid rgba(255, 255, 255, 0.1)",
                                }}
                            />
                            <button
                                onClick={handleCopy}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white p-2.5 rounded-lg transition-all duration-200 flex items-center justify-center"
                                style={{
                                    background: copied ? "rgba(76, 175, 80, 0.2)" : "rgba(77, 189, 255, 0.15)",
                                    border: `1px solid ${copied ? "rgba(76, 175, 80, 0.3)" : "rgba(77, 189, 255, 0.2)"}`,
                                }}
                                title={copied ? t('payment.copied') : t('payment.copyAddress')}
                            >
                                {copied ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </svg>
                                )}
                            </button>
                        </div>
                        {copied && (
                            <p className="text-[#4CAF50] text-xs mt-1.5 flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                {t('payment.copiedToClipboard')}
                            </p>
                        )}
                    </div>

                    {/* QR Code */}
                    {qrCodeUrl && (
                        <div className="flex flex-col items-center gap-3">
                            <p className="text-white/40 text-[10px] font-medium uppercase tracking-widest">{t('payment.scanQrCode')}</p>
                            <div className="p-3 rounded-2xl" style={{ background: "rgba(255, 255, 255, 0.95)" }}>
                                <img src={qrCodeUrl} alt="Address QR Code" className="w-[160px] h-[160px] rounded-lg" />
                            </div>
                        </div>
                    )}

                    {/* Warning */}
                    <div
                        className="flex items-start gap-3 p-3 rounded-xl w-full"
                        style={{ background: "rgba(239, 158, 63, 0.06)", border: "1px solid rgba(239, 158, 63, 0.12)" }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF9E3F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        <p className="text-white/50 text-xs leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: t('payment.onlySendWarning', { coin: selectedCoin?.coin || 'crypto', chain: selectedCoin?.chain || 'correct' }) }}
                        />
                    </div>

                    {/* Done button */}
                    <button
                        className="w-full text-white/60 hover:text-white text-sm font-semibold py-3 rounded-xl transition-all duration-200 hover:bg-white/[0.06] active:scale-[0.98]"
                        style={{ border: "1px solid rgba(255, 255, 255, 0.1)" }}
                        onClick={() => {
                            setAddress(null);
                            setQrCodeUrl("");
                        }}
                    >
                        {t('payment.generateNewAddress')}
                    </button>
                </div>
            ) : (
                <div className="flex flex-col gap-4 w-full">
                    <p className="text-white/50 text-xs font-medium">{t('payment.selectCrypto')}</p>
                    <CryptoSelect
                        options={data?.paymentMethodActions || []}
                        value={paymentMethodes}
                        onChange={(val) => setPaymentMethodes(val)}
                        placeholder={t('payment.selectCrypto')}
                    />

                    {/* Info hint */}
                    <div
                        className="flex items-center gap-2.5 p-3 rounded-xl"
                        style={{ background: "rgba(77, 189, 255, 0.05)", border: "1px solid rgba(77, 189, 255, 0.1)" }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4DBDFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <p className="text-white/40 text-xs leading-relaxed">
                            {t('payment.uniqueWalletInfo')}
                        </p>
                    </div>

                    <button
                        className="w-full text-white text-sm font-semibold py-3 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-[#0086D3]/25 active:scale-[0.98] mt-2 flex items-center justify-center gap-2"
                        style={{ background: "linear-gradient(135deg, #4DBDFF, #0086D3)" }}
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                {t('payment.generating')}
                            </>
                        ) : (
                            t('payment.generateAddress')
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CashIn;
